import styled, { css, keyframes } from 'styled-components';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'small' | 'medium' | 'large';

type ButtonStyledProps = {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $block: boolean;
  $hasIcon: boolean;
  $iconOnly: boolean;
};

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const sizeStyles = {
  small: css`
    height: 28px;
    padding: 0 12px;
    font-size: 13px;
    border-radius: 4px;

    svg {
      width: 14px;
      height: 14px;
    }
  `,
  medium: css`
    height: 36px;
    padding: 0 16px;
    font-size: 14px;
    border-radius: 6px;

    svg {
      width: 16px;
      height: 16px;
    }
  `,
  large: css`
    height: 44px;
    padding: 0 24px;
    font-size: 15px;
    border-radius: 8px;

    svg {
      width: 18px;
      height: 18px;
    }
  `,
};

const variantStyles = {
  primary: css`
    background: var(--primary);
    color: #fff;
    border: none;

    &:hover:not(:disabled) {
      background: color-mix(in srgb, var(--primary) 85%, #000);
    }

    &:active:not(:disabled) {
      background: color-mix(in srgb, var(--primary) 75%, #000);
    }
  `,
  secondary: css`
    background: #f5f5f5;
    color: rgba(0, 0, 0, 0.85);
    border: none;

    &:hover:not(:disabled) {
      background: #e8e8e8;
    }

    &:active:not(:disabled) {
      background: #d9d9d9;
    }
  `,
  outline: css`
    background: transparent;
    color: var(--primary);
    border: 1px solid var(--primary);

    &:hover:not(:disabled) {
      background: color-mix(in srgb, var(--primary) 8%, transparent);
    }

    &:active:not(:disabled) {
      background: color-mix(in srgb, var(--primary) 15%, transparent);
    }
  `,
  ghost: css`
    background: transparent;
    color: rgba(0, 0, 0, 0.65);
    border: none;

    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.04);
      color: rgba(0, 0, 0, 0.85);
    }

    &:active:not(:disabled) {
      background: rgba(0, 0, 0, 0.08);
    }
  `,
  danger: css`
    background: #ff4d4f;
    color: #fff;
    border: none;

    &:hover:not(:disabled) {
      background: #ff7875;
    }

    &:active:not(:disabled) {
      background: #d9363e;
    }
  `,
};

export const ButtonStyled = styled.button<ButtonStyledProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  user-select: none;

  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant }) => variantStyles[$variant]}

  ${({ $block }) =>
    $block &&
    css`
      width: 100%;
    `}

  ${({ $iconOnly, $size }) =>
    $iconOnly &&
    css`
      padding: 0;
      width: ${$size === 'small' ? '28px' : $size === 'medium' ? '36px' : '44px'};
    `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .loading-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: ${spin} 0.6s linear infinite;
  }
`;

type ButtonIconProps = {
  $position: 'left' | 'right';
};

export const ButtonIcon = styled.span<ButtonIconProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
